import { useState, useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { handlePutRequest, handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator.js'
import { showToaster } from '../../utils/show-toaster.util'
import SubmittedMessage from './SubmittedMessage.jsx'
import { useParams } from 'react-router-dom'
import EditablePriceField from './EditablePriceField.jsx'
import ProductCategoryDisplay from './ProductCategoryDisplay.jsx'
import ProductDescriptionDisplay from './ProductDescriptionDisplay.jsx'
import ProductImageDisplay from './ProductImageDisplay.jsx'
import ProductNameDisplay from './ProductNameDisplay.jsx'
import { useAuth } from '../../contexts/AuthContext.js'
import useProductCategories from '../../hooks/useProductCategories.js'
import DisplayForUserType from '../auth/DisplayForUserType.jsx'
import USER_TYPE from '../../constants/userType.js'
import InputField from '../common/InputField.jsx'

const ProductEditForm = () => {
  const { categories } = useProductCategories()
  const { currentUser } = useAuth()
  const userType = currentUser?.userType
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [productPrice, setProductPrice] = useState('')
  const [minOrderQuantity, setMinOrderQuantity] = useState('')

  const { productSlug } = useParams()

  const { data: productData } = useQuery({
    queryKey: QueryKeys.shopProductDetails(userType, productSlug),
    queryFn: () => handleGetRequest(`/${userType}s/my-products/${productSlug}`),
  })

  const product = productData?.data

  useMemo(() => {
    setProductPrice(product?.price || '')
    setMinOrderQuantity(product?.minOrderQuantity || 1)
  }, [product?.price, product?.minOrderQuantity])

  const retailerProductMutation = useMutation({
    mutationFn: (payload) => handlePutRequest(`/${userType}s/my-products/${productSlug}`, payload),
    onSuccess: () => {
      QueryKeys.shopProductDetails.invalidate(userType, productSlug)
      QueryKeys.listShopProducts.invalidateAll()
      showToaster('success', 'Price updated successfully!')
      setIsSubmitted(true)
    },
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    await retailerProductMutation.mutateAsync({
      price: productPrice,
      minOrderQuantity: minOrderQuantity,
    })
  }

  if (!product) {
    return <div className="text-center py-10">Product not found</div>
  }

  return isSubmitted ? (
    <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
      <SubmittedMessage
        message="Product updated"
        buttonMessage="View Product"
        redirectPath={`/${userType}/products/${productSlug}`}
      />
    </div>
  ) : (
    <div>
      <form onSubmit={handleSubmit} className="mt-4">
        <fieldset disabled={retailerProductMutation.isLoading}>
          <ProductNameDisplay name={product.name} />
          <ProductCategoryDisplay
            categoryId={product?.categoryId}
            categories={categories}
            disabled={true}
          />
          <EditablePriceField
            price={productPrice}
            setPrice={setProductPrice}
            isLoading={retailerProductMutation.isLoading}
          />
          <DisplayForUserType userTypes={[USER_TYPE.wholeseller]}>
            <InputField
              id="minOrderQuantity"
              name="minOrderQuantity"
              type="number"
              value={minOrderQuantity}
              onChange={(e) => setMinOrderQuantity(e.target.value)}
              required
              placeholder="Enter minimum order quantity"
              label="Minimum Order Quantity"
              min="1"
            />
          </DisplayForUserType>
          <ProductDescriptionDisplay description={product.description} />
          <ProductImageDisplay imageUrl={product.primaryImageUrl} />

          <div className="pt-4 mt-8">
            <button
              type="submit"
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 ${
                retailerProductMutation.isLoading ? 'opacity-70 cursor-wait' : ''
              }`}
            >
              {retailerProductMutation.isLoading ? 'Updating...' : 'Update product'}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}

export default ProductEditForm
